import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import QRCode from 'qrcode';
import { toast } from 'sonner';
import { Copy, Share2, Loader2, CheckCircle2, X, Smartphone } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PayFromAnotherPhone = ({ registrationId, onClose }) => {
  const [payerMobile, setPayerMobile] = useState('');
  const [sending, setSending] = useState(false);
  const [link, setLink] = useState(null);
  const [qr, setQr] = useState('');
  const [memberId, setMemberId] = useState(null);

  const sendRequest = async () => {
    const digits = payerMobile.replace(/\D/g, '').slice(-10);
    if (!/^[6-9]\d{9}$/.test(digits)) { toast.error("Please enter the payer's 10-digit mobile number"); return; }
    setSending(true);
    try {
      const r = await axios.post(`${API}/registration/${registrationId}/payment-link`, { payer_mobile: digits });
      setLink(r.data);
      setQr(await QRCode.toDataURL(r.data.short_url, { width: 240, margin: 1 }));
      toast.success('Payment request sent by SMS');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not send the payment request. Please try again.');
    } finally {
      setSending(false);
    }
  };

  useEffect(() => {
    if (!link || memberId) return undefined;
    const t = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/registration/${registrationId}/status`);
        if (r.data.status === 'completed') setMemberId(r.data.member_id);
      } catch { /* keep polling */ }
    }, 5000);
    return () => clearInterval(t);
  }, [link, memberId, registrationId]);

  const share = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: 'BITZ Club membership payment', url: link.short_url }); } catch { /* cancelled */ }
    } else {
      await navigator.clipboard.writeText(link.short_url);
      toast.success('Payment link copied');
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" data-testid="pay-other-phone-modal">
      <div className="w-full max-w-sm rounded-2xl bg-[#0F0F10] border border-[#D4AF37]/40 p-6 relative text-center">
        <button onClick={onClose} className="absolute top-3 right-3 text-gray-400 hover:text-white" data-testid="pay-other-phone-close"><X className="w-5 h-5" /></button>
        {memberId ? (
          <div data-testid="pay-other-phone-success">
            <CheckCircle2 className="w-14 h-14 text-emerald-400 mx-auto" />
            <h3 className="text-xl font-semibold text-white mt-3">Payment received</h3>
            <p className="text-gray-300 mt-2">Membership created. Member ID:</p>
            <p className="text-2xl font-mono text-[#D4AF37] mt-1" data-testid="pay-other-phone-member-id">{memberId}</p>
            <p className="text-xs text-gray-400 mt-3">Log in with this Member ID as both username and password.</p>
            <a href="/member/login" className="btn-primary w-full mt-5 inline-block" data-testid="pay-other-phone-login">Go to Login</a>
          </div>
        ) : !link ? (
          <div data-testid="pay-other-phone-form">
            <Smartphone className="w-10 h-10 text-[#D4AF37] mx-auto" />
            <h3 className="text-lg font-semibold text-white mt-2">Someone else will pay</h3>
            <p className="text-sm text-gray-400 mt-1">Enter the payer's mobile number. Razorpay will send them the payment request by SMS.</p>
            <input
              type="tel" inputMode="numeric" maxLength={14} value={payerMobile}
              onChange={(e) => setPayerMobile(e.target.value)} placeholder="Payer's mobile number"
              className="input-gold w-full mt-4 text-center" data-testid="pay-other-phone-mobile"
            />
            <button onClick={sendRequest} disabled={sending} className="btn-primary w-full mt-4 flex items-center justify-center gap-2" data-testid="pay-other-phone-send">
              {sending && <Loader2 className="w-4 h-4 animate-spin" />} Send payment request
            </button>
          </div>
        ) : (
          <>
            <h3 className="text-lg font-semibold text-white">Payment request sent</h3>
            <p className="text-sm text-gray-400 mt-1" data-testid="pay-other-phone-sent-to">Sent to ******{(link.payer_mobile || '').slice(-4)} · Amount ₹{link.amount}</p>
            <p className="text-xs text-gray-500 mt-3">They can also scan this code or open the link:</p>
            {qr && <img src={qr} alt="Payment QR code" className="mx-auto mt-2 rounded-lg bg-white p-2 w-44 h-44" data-testid="pay-other-phone-qr" />}
            <div className="flex gap-2 mt-4">
              <button onClick={() => { navigator.clipboard.writeText(link.short_url); toast.success('Payment link copied'); }} className="btn-secondary flex-1 flex items-center justify-center gap-2 text-sm whitespace-nowrap" data-testid="pay-other-phone-copy"><Copy className="w-4 h-4" /> Copy link</button>
              <button onClick={share} className="btn-primary flex-1 flex items-center justify-center gap-2 text-sm whitespace-nowrap" data-testid="pay-other-phone-share"><Share2 className="w-4 h-4" /> Share</button>
            </div>
            <p className="text-xs text-gray-500 mt-4 flex items-center justify-center gap-2" data-testid="pay-other-phone-waiting"><Loader2 className="w-3 h-3 animate-spin" /> Waiting for payment... keep this screen open</p>
          </>
        )}
      </div>
    </div>,
    document.body
  );
};
